import { ActivityIndicator } from 'react-native-paper';
import React, { useState, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { View, TextInput, TouchableOpacity, Pressable, Text, Dimensions } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import * as Progress from 'react-native-progress';
import { ScrollView } from 'react-native';
import { saveFile, saveStorageBlob } from '@/utils/FileManager'
import { areStringsPopulated } from '@/utils/InputValidator';
import { Query } from '@/utils/react-query/Queries';
import * as Crypto from 'expo-crypto';
import { Image } from 'expo-image';
import MentionPicker from '@/components/Connect/MentionPicker';
import { encodeMention, stripMentions } from '@/utils/mentionFormat';
import { getMentionEntity } from '@/utils/constants/mentionEntities';
import { choosePhoto } from '@/utils/FileManager';
import useMessagesStore from '@/data/store/useMessagesStore';
import usePlatform from '@/utils/hooks/usePlatform';
import EmojiPicker, { emojisByCategory } from 'rn-emoji-keyboard';
import { excludedEmojis } from '@/utils/constants/excludedEmojis';
import SectionTitle from '@/components/General/SectionTitle';
import SearchBox from '@/components/General/inputs/SearchBox';
import { FlashList } from '@shopify/flash-list';
import SheetModal from '@/components/General/SheetModal';
import klipyAttributionImage from '@/assets/klipy.png';
import DropdownMenu from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';

//Render the message text with confirmed mention labels colored in their module color.
//Used as an overlay behind a transparent TextInput so the user sees colored mention text
//while typing. Returns an array of <Text> nodes.
const renderColoredMentions = (text, mentionList, colors) => {
    if(!text) return null;
    if(!mentionList || mentionList.length === 0) return <Text style={{color: colors.text}}>{text}</Text>;

    //Find all mention label positions in the text
    const segments = [];
    let remaining = text;
    const found = [];

    mentionList.forEach(m => {
        let searchFrom = 0;
        while(true){
            const idx = remaining.indexOf(m.label, searchFrom);
            if(idx === -1) break;
            found.push({ start: idx, end: idx + m.label.length, label: m.label, type: m.type });
            searchFrom = idx + m.label.length;
        }
    });

    if(found.length === 0) return <Text style={{color: colors.text}}>{text}</Text>;

    //Sort by start position and dedupe overlaps (first occurrence wins)
    found.sort((a, b) => a.start - b.start);
    const nonOverlapping = [];
    let lastEnd = 0;
    for(const f of found){
        if(f.start >= lastEnd){
            nonOverlapping.push(f);
            lastEnd = f.end;
        }
    }

    //Build segments
    let cursor = 0;
    for(const f of nonOverlapping){
        if(f.start > cursor){
            segments.push(<Text key={`t-${cursor}`} style={{color: colors.text}}>{remaining.slice(cursor, f.start)}</Text>);
        }
        const entity = getMentionEntity(f.type);
        const color = entity ? colors[entity.color] : colors.primary;
        segments.push(<Text key={`m-${f.start}`} style={{ color }}>{f.label}</Text>);
        cursor = f.end;
    }
    if(cursor < remaining.length){
        segments.push(<Text key={`t-${cursor}`} style={{color: colors.text}}>{remaining.slice(cursor)}</Text>);
    }

    return <>{segments}</>;
};

const MessageInput = forwardRef(({ placeholder = "Type a message...", onSendMessage, members, onBlur = () => {}, enableAttachments = false, attachmentDirectory, onFocus = () => {}, detection = [], bottomInset = 0, inputRef: externalInputRef, hideReplyBanner = false, style, embedded = false, nativeID, extraContentPadding, hideSendButton = false, fixedMentionPicker = false, initialText = '', initialMentions = [] }, ref) => {
    const [message, setMessage] = useState(initialText);
    const messageRef = useRef(initialText);
    const inputRowWrapperRef = useRef(null);
    const [attachments, setAttachments] = useState([]);
    const [processing, setProcessing] = useState(false);
    //Confirmed mentions: [{ type, id, label }] — matched to `message` text by label on send.
    const [mentions, setMentions] = useState(initialMentions || []);
    const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [gifSearchText, setGifSearchText] = useState("");
    const overlayScrollY = useSharedValue(0);
    const overlayAnimatedStyle = useAnimatedStyle(() => ({
        transform: [{ translateY: -overlayScrollY.value }],
    }));

    const [showPicker, setShowPicker] = useState(false);
    const [pickerTerm, setPickerTerm] = useState("");
    const [mentionStart, setMentionStart] = useState(null); //index of the active '@'
    const [gifModalOpen, setGifModalOpen] = useState(false);
    const isFetchingGifsRef = useRef(false);

    const storeInputRef = useMessagesStore((state) => state.inputRef);
    const inputRef = externalInputRef || storeInputRef;
    const replyingToMessage = useMessagesStore((state) => state.replyingToMessage);
    const setReplyingToMessage = useMessagesStore((state) => state.setReplyingToMessage);

    const { theme } = useUnistyles();
    const { isWeb } = usePlatform();

    //Update extraContentPadding shared value when the TextInput height changes (multiline growth).
    //KeyboardChatScrollView uses this to extend the scrollable range so last messages aren't
    //hidden under a taller input. The value is the delta above the baseline minimum height.
    const MIN_INPUT_HEIGHT = 40;
    const onInputLayout = useCallback((e) => {
        if(extraContentPadding){
            const height = e.nativeEvent.layout.height;
            extraContentPadding.value = withTiming(
                Math.max(height - MIN_INPUT_HEIGHT, 0),
                { duration: 250 }
            );
        }
    }, [extraContentPadding]);


    const {data: loggedInUser} = Query.people.loggedInUser();
    const {data: gifs, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.gifs.all({searchTerm: gifSearchText, options: {enabled: !!gifSearchText}});

    const flatGifs = useMemo(() => gifs?.pages.flatMap(page => page.results), [gifs]);

    const emojisToShow = useMemo(() => {
        const emojis = emojisByCategory.map(emojis => ({...emojis, data: emojis.data.filter(emoji => !excludedEmojis.includes(emoji.name))}));
        return emojis;
    }, []);

    const mentionsEnabled = Array.isArray(detection) ? detection.includes("mention") : false;

    //Detect an active "@..." query ending at the cursor. Returns {at, term} or null.
    //The '@' must be at the start of the text or preceded by whitespace (avoids emails).
    const detectActiveMention = (text, cursor) => {
        const cur = typeof cursor === "number" ? cursor : text.length;
        const uptoCursor = text.slice(0, cur);
        const at = uptoCursor.lastIndexOf("@");
        if(at === -1) return null;

        const before = at > 0 ? uptoCursor[at - 1] : " ";
        if(!/\s/.test(before)) return null;

        const term = uptoCursor.slice(at + 1);
        if(term.includes("\n") || term.length > 40) return null;

        return { at, term };
    };

    //Build the token-embedded text for persistence. Each confirmed mention's label
    //is swapped for its ⟦type:id:label⟧ token. Uses placeholders so duplicate labels
    //and labels-inside-tokens don't collide.
    const buildTokenText = (msg, mentionList) => {
        let out = msg;
        const swaps = [];
        mentionList.forEach((m, i) => {
            const idx = out.indexOf(m.label);
            if(idx === -1) return;
            const ph = `\u0000${i}\u0000`;
            out = out.slice(0, idx) + ph + out.slice(idx + m.label.length);
            swaps.push({ ph, token: encodeMention(m) });
        });
        swaps.forEach(({ ph, token }) => { out = out.replace(ph, token); });
        return out;
    };

    const handleSendMessage = async () => {
        try {
            if(enableAttachments && attachments.length > 0){
                let attachmentRequests = [];

                for(let i=0; i<attachments.length; i++){
                    if(attachments[i].uri?.startsWith('http')){
                        attachmentRequests[i] = {key: attachments[i].uri};
                    }
                    else if(attachments[i].uri?.startsWith('blob')){
                        attachmentRequests[i] = await saveStorageBlob(attachments[i].blob, `${loggedInUser?.organizationID}/${attachmentDirectory}/${Crypto.randomUUID()}.${attachments[i].extension}`);
                    }
                    else{
                        attachmentRequests[i] = await saveFile(attachments[i].uri, `${loggedInUser?.organizationID}/${attachmentDirectory}/${Crypto.randomUUID()}.${attachments[i].extension}`);
                    }
                }

                const results = await Promise.all(attachmentRequests);
                let attachment_keys = [];

                for(let i=0; i<results.length; i++){
                    if(results[i]?.key){
                        attachment_keys[i] = results[i].key;
                    }
                }

                const tokenText = buildTokenText(message, mentions);
                onSendMessage(tokenText, attachment_keys);
                setMentions([]);
                messageRef.current = '';
                setMessage('');
                setAttachments([]);
            }
            else{
                if(areStringsPopulated([message])){
                    const tokenText = buildTokenText(message, mentions);
                    onSendMessage(tokenText, []);
                    setMentions([]);
                    messageRef.current = '';
                    setMessage('');
                }
            }
        }
        catch(error){
            //Send or upload failed — keep text/attachments so the user can retry
            console.warn('Message send failed:', error);
        }
    };


    const removeAttachment = (index) => {
        let newAttachments = [...attachments];
        newAttachments.splice(index, 1);

        setAttachments(newAttachments);
    }

    const handlePick = (emoji) => {
        setShowEmojiPicker(false);

        const before = message.slice(0, cursorPosition.start);
        const after = message.slice(cursorPosition.end);
        const newText = before + emoji?.emoji + after;
        const cursorPos = cursorPosition.start + emoji?.emoji?.length;

        messageRef.current = newText;
        setMessage(newText);
        setCursorPosition([cursorPos, cursorPos]);
    }

    const updateText = (text) => {
        const prev = message;
        const charDiff = text.length - prev.length;
        const approxCursor = (cursorPosition?.start ?? prev.length) + charDiff;

        messageRef.current = text;
        setMessage(text);

        //Drop confirmed mentions whose label text no longer exists in the message
        setMentions(prevMentions => prevMentions.filter(m => text.includes(m.label)));

        if(!mentionsEnabled){
            setShowPicker(false);
            return;
        }

        const active = detectActiveMention(text, approxCursor);
        if(active){
            setShowPicker(true);
            setPickerTerm(active.term);
            setMentionStart(active.at);
        }
        else{
            setShowPicker(false);
            setPickerTerm("");
            setMentionStart(null);
        }
    };

    //Insert a confirmed mention: replace the active "@term" with the entity label.
    const confirmMention = (entity) => {
        if(mentionStart == null) return;
        const replaceEnd = mentionStart + 1 + pickerTerm.length;
        const newMsg = message.slice(0, mentionStart) + entity.label + " " + message.slice(replaceEnd);

        messageRef.current = newMsg;
        setMessage(newMsg);
        setMentions(prev => [...prev, { type: entity.type, id: entity.id, label: entity.label }]);
        setShowPicker(false);
        setPickerTerm("");
        setMentionStart(null);
        inputRef?.current?.focus?.();
    };

    //Remove a confirmed mention chip and delete its label text from the message.
    const removeMention = (index) => {
        const m = mentions[index];
        if(m){
            const idx = message.indexOf(m.label);
            if(idx !== -1){
                const newMsg = message.slice(0, idx) + message.slice(idx + m.label.length);
                messageRef.current = newMsg;
                setMessage(newMsg);
            }
        }
        setMentions(prev => prev.filter((_, i) => i !== index));
    };

    useImperativeHandle(ref, () => ({
        getMessage: () => message,
        getAttachments: () => attachments,
        sendMessage: () => handleSendMessage(),
        setMessage: (text) => { messageRef.current = text; setMessage(text); },
        setMentions: (list) => setMentions(list || []),
    }), [message, attachments, handleSendMessage, setMessage, setMentions]);

    const attachmentPreviews = !enableAttachments ? [] : attachments.map((attachment, i) => {
        return <View key={"attachmentPreview" + i} style={styles.attachmentPreview}>
            <TouchableOpacity onPress={() => removeAttachment(i)} style={styles.removeAttachmentButton}>
                <Unicon name="close-circle" colorVariant="text_on_color" size={20}/>
            </TouchableOpacity>
            {attachment.type == 'video' && <View style={styles.videoIconOverlay}><Unicon name="videocam" colorVariant="text_on_color" size={24}/></View>}
            <Image contentFit="cover" source={{uri: attachment.thumbnailUri}} style={styles.previewImage}/>
        </View>
    })


  return (
    <View style={[styles.container, isWeb && { paddingBottom: bottomInset }, embedded && styles.embedded, style]}>
        <View style={styles.inputContainer}>
            <View style={styles.progressBarContainer}><Progress.Bar width={null} borderWidth={0} borderRadius={0} height={3} color={theme.colors.tertiary} indeterminate={processing}/></View>

            {replyingToMessage && !hideReplyBanner &&
                <View style={styles.replyingContainer}>
                    <View style={styles.replyingContent}>
                        <Pressable style={styles.closeReplying} onPress={() => setReplyingToMessage(null)}>
                            <Unicon name="close" size={20} colorVariant="textSecondary" />
                        </Pressable>

                        <SectionTitle title="Replying to" style={{fontSize:12}}/>
                        <Text style={styles.replyingName}>{replyingToMessage?.Sender?.firstName + " " + replyingToMessage?.Sender?.lastName}</Text>
                        <Text numberOfLines={1} style={styles.replyingText}>{stripMentions(replyingToMessage?.text)}</Text>
                    </View>
                </View>
            }

            {mentions.length > 0 &&
                <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled" style={styles.mentionChipsRow} contentContainerStyle={styles.mentionChipsContent}>
                    {mentions.map((m, i) => {
                        const entity = getMentionEntity(m.type);
                        const color = entity ? theme.colors[entity.color] : theme.colors.primary;
                        return (
                            <View key={`${m.type}-${m.id}-${i}`} style={styles.mentionChip(color)}>
                                {entity && <Unicon name={entity.icon} size={12} color={color} />}
                                <Text style={styles.mentionChipText(color)} numberOfLines={1}>{m.label}</Text>
                                <Pressable hitSlop={6} onPress={() => removeMention(i)}>
                                    <Unicon name="close" size={13} color={color} />
                                </Pressable>
                            </View>
                        );
                    })}
                </ScrollView>
            }

            <View ref={inputRowWrapperRef} style={styles.inputRowWrapper}>
                { showPicker &&
                    <MentionPicker
                        term={pickerTerm}
                        members={members || null}
                        onConfirm={confirmMention}
                        onDismiss={() => setShowPicker(false)}
                        anchorRef={fixedMentionPicker ? inputRowWrapperRef : undefined}
                    />
                }

                <View style={[styles.input, !embedded && styles.inputMaxHeight]}>
                <View style={styles.textWrapper}>
                    {mentions.length > 0 && message.length > 0 && (
                        <Animated.Text
                            style={[styles.textOverlay, overlayAnimatedStyle]}
                            pointerEvents="none"
                        >
                            {renderColoredMentions(message, mentions, theme.colors)}
                        </Animated.Text>
                    )}
                    <TextInput
                        testID="message-input"
                        nativeID={nativeID}
                        autoCapitalize="sentences"
                        autoCorrect
                        multiline
                        ref={inputRef || null}
                        value={message}
                        style={[styles.textInput, mentions.length > 0 && styles.textInputTransparent]}
                        onChangeText={updateText}
                        onLayout={onInputLayout}
                        onScroll={(e) => {
                            // Web (Safari) synthetic scroll events don't always populate
                            // nativeEvent.contentOffset — guard to avoid TypeError. See CHURCH-APP-16H.
                            overlayScrollY.value = e.nativeEvent?.contentOffset?.y ?? 0;
                        }}
                        onKeyPress={ (event) => {
                            if(!hideSendButton && isWeb && event.nativeEvent.key == "Enter" && event.nativeEvent.shiftKey == false){
                                event.preventDefault();
                                message.trim() == '' && attachments.length == 0 ? null : handleSendMessage()
                            } 
                        }}
                        onSelectionChange={(e) => {
                            const sel = e.nativeEvent.selection;
                            setCursorPosition(sel);

                            //Re-run mention detection with the updated cursor — on web,
                            //onSelectionChange can fire before onChangeText, so this catches
                            //cases where the cursor position was stale during updateText.
                            //Uses messageRef (not message state) to avoid stale closures.
                            if(mentionsEnabled){
                                const active = detectActiveMention(messageRef.current, sel?.start);
                                if(active){
                                    setShowPicker(true);
                                    setPickerTerm(active.term);
                                    setMentionStart(active.at);
                                }
                                else if(showPicker){
                                    setShowPicker(false);
                                    setPickerTerm("");
                                    setMentionStart(null);
                                }
                            }
                        }}
                        placeholder={placeholder}
                        placeholderTextColor={theme.colors.textSecondary}
                        onFocus={(e) => {
                            onFocus(e);
                        }}
                        onBlur={(e) => {
                            onBlur(e);
                        }}
                    />
                </View>

                <View style={styles.actionsContainer}>
                    <View style={{}}>
                        <Pressable onPress={() => setShowEmojiPicker(true)}><Unicon name="happy-outline" size={20} colorVariant="textSecondary" /></Pressable>
                    </View>

                    { enableAttachments &&
                        <>
                            <DropdownMenu triggerComponent={<Unicon name="image-outline" size={20} colorVariant="textSecondary" />} options={[
                                {name: "Upload", onSelect: async () => {
                                    setProcessing(true);
                                    const assets = await choosePhoto({multiple: true, selectionLimit: 5, maxSize: 125, quality: 0.3});
                                    setProcessing(false);
                                    if(assets && assets.length > 0){
                                        setAttachments(prev => {
                                            return [...prev, ...assets]
                                        })
                                    }
                                }},
                                {name: "GIF", onSelect: () => {
                                    setGifModalOpen(true);
                                }}
                            ]}/>
                        </>
                    }

                    {!hideSendButton && (
                        <Pressable testID="send-message" onPress={() => message.trim() == '' && attachments.length == 0 ? null : handleSendMessage()} style={styles.sendButton(message.trim() == '' && attachments.length == 0)}>
                            <Unicon name="send" size={16} colorVariant="onPrimary" />
                        </Pressable>
                    )}
                </View>

            </View>
            </View>

        </View>

        { enableAttachments &&
            <ScrollView horizontal keyboardShouldPersistTaps="handled">
                <View style={styles.attachmentScroll}>
                    {attachmentPreviews}
                </View>
            </ScrollView>
        }


        <SheetModal title="Send a GIF" isOpen={gifModalOpen} onClose={() => setGifModalOpen(false)} snapPoints={[700]}>
            <View>
                <SearchBox
                    searchText={gifSearchText}
                    placeholder="Search KLIPY"
                    onChangeText={(e) => setGifSearchText(e)}
                    textChangeDelay={1000}
                />

                {(!gifSearchText && !flatGifs) && <Text style={styles.emptyGifText}>Start typing to search for a GIF</Text>}

                <View style={styles.gifListContainer}>
                    <FlashList
                        data={flatGifs || []}
                        onEndReached={() => {
                            if(!isFetchingGifsRef.current && !isFetchingNextPage && hasNextPage){
                                isFetchingGifsRef.current = true;
                                fetchNextPage().finally(() => { isFetchingGifsRef.current = false; });
                            }
                        }}
                        onEndReachedThreshold={0.3}
                        initialNumToRender={6}
                        maxToRenderPerBatch={6}
                        windowSize={5}
                        numColumns={2}
                        estimatedItemSize={200}
                        overrideItemLayout={(layout, item) => {
                            const colWidth = (Dimensions.get('window').width - 24) / 2;
                            const aspectRatio = item?.width && item?.height ? item.width / item.height : 1;
                            layout.size = Math.min(colWidth / aspectRatio, 250);
                        }}
                        renderItem={({item}) => {
                            const colWidth = (Dimensions.get('window').width - 24) / 2;
                            const aspectRatio = item?.width && item?.height ? item.width / item.height : 1;
                            const height = Math.min(colWidth / aspectRatio, 250);
                            return <Pressable style={styles.gifItem} onPress={() => {
                                setAttachments(prev => {
                                    const newAttachment = {...item, thumbnailUri: item?.url, uri: item?.url, type: 'image', extension: 'gif'};
                                    return [...prev, newAttachment]
                                })

                                setGifModalOpen(false);
                            }}><Image source={{uri: item?.url}} style={{width: colWidth - 8, height, borderRadius: 8}}/></Pressable>
                        }}
                        ListFooterComponent={
                            isFetchingNextPage ? <ActivityIndicator /> : null
                        }
                    />

                    <View style={styles.attributionContainer}>
                        <Image contentFit='contain' source={klipyAttributionImage} style={styles.attributionImage}/>
                    </View>
                </View>
            </View>
        </SheetModal>

        <EmojiPicker 
            onEmojiSelected={handlePick} 
            open={showEmojiPicker} 
            emojisByCategory={emojisToShow}
            onClose={() => setShowEmojiPicker(false)}
            enableSearchBar
            categoryPosition="bottom"
            enableRecentlyUsed
            categoryOrder={["recently_used"]}
            styles={{
                searchBar: {
                    container: {
                        borderRadius: 8
                    }
                }
            }}
            theme={{
                backdrop: '#16161888',
                knob: theme.colors.onSurface,
                container: theme.colors.surface,
                header: theme.colors.text,
                skinTonesContainer: theme.colors.background,
                category: {
                  icon: theme.colors.primary,
                  iconActive: theme.colors.surface,
                  container: theme.colors.surface,
                  containerActive: theme.colors.primary,
                },
                search: {
                    background: theme.colors.background,
                    text: theme.colors.text,
                    placeholder: theme.colors.textSecondary,
                    icon: theme.colors.textSecondary
                }
            }}
        />

    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
    container: {
        position: "relative",
        backgroundColor: "transparent",
    },
    embedded: {
        position: "relative",
        bottom: "auto",
    },
    inputContainer: {
        paddingHorizontal: 10,
        paddingTop: 10,
        paddingBottom: 30,
        flexGrow: 1,
        alignItems: "center",
        width: "100%",
    },
    inputRowWrapper: {
        width: "100%",
        maxWidth: 700,
        position: "relative",
    },
    progressBarContainer: {
        position: "absolute",
        top: 0
    },
    replyingContainer: {
        backgroundColor: theme.colors.background,
        borderRadius: 8,
        padding: 10,
        margin: 5
    },
    replyingContent: {
        borderLeftWidth: 3,
        borderLeftColor: theme.colors.secondary,
        paddingLeft: 10
    },
    closeReplying: {
        position: "absolute",
        top: 0,
        right: 0,
        zIndex: 3
    },
    replyingName: {
        color: theme.colors.text,
        fontSize: 12,
        fontFamily: theme.fonts.bold
    },
    replyingText: {
        color: theme.colors.text,
        fontSize: 11,
        fontStyle: "italic"
    },
    input: {
        paddingLeft: 16,
        paddingRight: 8,
        borderRadius: 22,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors.surface,
        minHeight: 44,
        fontSize: 15,
        color: theme.colors.text,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        maxWidth: 700,
        width: "100%"
    },
    inputMaxHeight: {
        maxHeight: 120,
    },
    textWrapper: {
        flex: 1,
        justifyContent: "center",
        overflow: "hidden",
    },
    icons: {
        alignItems: "center",
        justifyContent: "center"
    },
    mentionChipsRow: {
        maxHeight: 40,
        marginHorizontal: 5,
        marginBottom: 4,
    },
    mentionChipsContent: {
        alignItems: "center",
        gap: 6,
        paddingVertical: 2,
    },
    mentionChip: (color) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 100,
        borderWidth: 1,
        borderColor: color,
        backgroundColor: color + "1A",
    }),
    mentionChipText: (color) => ({
        color: color,
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        maxWidth: 140,
    }),
    attachmentPreview: {
        width: 100,
        height: 100,
        borderRadius: 12,
        overflow: "hidden",
        margin: 5,
    },
    removeAttachmentButton: {
        position: "absolute",
        top: 4,
        right: 4,
        zIndex: 2,
        backgroundColor: 'rgba(0,0,0,0.5)',
        borderRadius: 50,
        padding: 2,
    },
    videoIconOverlay: {
        position: "absolute",
        top: 0,
        right: 0,
        left: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
    },
    previewImage: {
        width: '100%',
        height: '100%',
    },
    textInput: {
        color: theme.colors.text,
        caretColor: theme.colors.text,
        fontSize: 15,
        lineHeight: 20,
        paddingTop: 12,
        paddingBottom: 12,
        outlineStyle: 'none',
        zIndex: 1000
    },
    textInputTransparent: {
        color: 'transparent',
    },
    textOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        fontSize: 15,
        lineHeight: 20,
        paddingTop: 12,
        paddingBottom: 12,
        zIndex: 999,
        overflow: 'hidden',
    },
    actionsContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingLeft: 8
    },
    sendButton: (disabled) => ({
        width: 34,
        height: 34,
        borderRadius: 17,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: disabled ? theme.colors.border : theme.colors.primary,
    }),
    attachmentScroll: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        flexDirection: "row",
        alignItems: "center",
    },
    emptyGifText: {
        paddingTop: 30,
        fontSize: 14,
        fontStyle: "italic",
        textAlign: "center",
        color: theme.colors.textSecondary
    },
    gifListContainer: {
        paddingTop: 10,
        flex: 1,
    },
    gifItem: {
        margin: 2,
        alignItems: 'center',
    },
    attributionContainer: {
        backgroundColor: theme.colors.background,
        width: "100%",
        paddingHorizontal: 10,
        paddingBottom: 10,
        paddingTop: 6,
        alignItems: 'flex-end'
    },
    attributionImage: {
        width: 60,
        height: 24
    }
}))

export default MessageInput;
